'use client';

import { FiltersType, SearchTabs } from '@/types';
import { useTranslations } from 'next-intl';
import { FC, useState, useEffect, useRef } from 'react';
import { LOCAL_FILTERS } from '../../HousingListScreen';
import { useAuth } from '@/contexts';
import { showToast } from '@/utils/showToast';
import { COLORS, PATHS } from '@/constants';
import { useFormik } from 'formik';
import {
  FORM_INITIAL_VALUES,
  SaveSearchProfileFormData,
  saveSearchProfileSchema,
} from './constants/schemas';
import { getActiveFiltersCount, getFilterObject, getProjectFilterObject } from '@/common';
import { Box, Button, FormControlLabel, Switch, Typography } from '@mui/material';
import {
  ModalContent,
  ModalControlBox,
  ModalHead,
  StyledButton,
  StyledControlButton,
  StyledModal,
} from './styles';
import AddIcon from '@mui/icons-material/Add';
import { CrossIcon, SearchProfileIcon, TextField } from '@/components';
import { useRouter } from 'next/navigation';
import { useCreateSearchProfile } from '@/queris';
import { useIntersectionObserver } from '@/hooks';

type SearchProfileProps = {
  fullWidth?: boolean;
  variant?: 'default' | 'mobileSticky' | 'mobileBar';
  filters: FiltersType;
  viewType: SearchTabs;
  isLoading?: boolean;
  isProject?: boolean;
  elementIsInViewport?: (check: boolean) => void;
};

export const SearchProfile: FC<SearchProfileProps> = ({
  fullWidth,
  variant = 'default',
  filters,
  isLoading,
  viewType,
  isProject,
  elementIsInViewport,
}) => {
  const t = useTranslations();
  const [searchProfileRef, isSearchProfileVisible] = useIntersectionObserver<HTMLButtonElement>();

  useEffect(() => {
    if (typeof elementIsInViewport === 'function') {
      elementIsInViewport(isSearchProfileVisible);
    }
  }, [isSearchProfileVisible]);

  const { user, isAuthenticated, login } = useAuth();
  const router = useRouter();

  const [savedFilters, setSavedFilters] = useState<FiltersType | null>(() => {
    if (typeof window === 'undefined') return null;
    const str = (localStorage.getItem(LOCAL_FILTERS) || '').trim();
    if (!str) return null;
    try {
      return JSON.parse(str) as FiltersType;
    } catch (e) {
      console.error('Failed to parse saved filters', e);
      return null;
    }
  });
  const [isOpen, setIsOpen] = useState(() => savedFilters !== null);

  useEffect(() => {
    localStorage.removeItem(LOCAL_FILTERS);
  }, []);

  const onSuccess = () => {
    const message = t('searchProfile.messages.createdSuccessfully');
    showToast.success(message);

    localStorage.removeItem(LOCAL_FILTERS);
    if (variant === 'mobileSticky') {
      setIsOpen(false);
      setSavedFilters(null);
    } else {
      router.push(`/${PATHS.PROFILE}/${PATHS.SEARCH_PROFILE}`);
    }
  };

  const { mutate, isPending } = useCreateSearchProfile(() => onSuccess());

  const isProjectRef = useRef(isProject);
  isProjectRef.current = isProject;

  const savedFiltersRef = useRef(savedFilters);
  savedFiltersRef.current = savedFilters;

  const formik = useFormik({
    initialValues: FORM_INITIAL_VALUES,
    validationSchema: saveSearchProfileSchema(t),
    onSubmit: (data: SaveSearchProfileFormData) => {
      const filtersToSave = savedFiltersRef.current || filters;
      const updatedFilters = isProjectRef.current
        ? getProjectFilterObject(filtersToSave)
        : getFilterObject(filtersToSave);
      const updatedProfile = {
        ...data,
        filters: { ...updatedFilters },
        viewType,
      };
      mutate({ profileObj: updatedProfile });
    },
  });

  const handleOpenModal = () => {
    if (isAuthenticated) {
      return setIsOpen(true);
    } else {
      localStorage.setItem(LOCAL_FILTERS, JSON.stringify(filters));
      login();
    }
  };

  const handleCloseModal = () => {
    formik.resetForm();
    setIsOpen(false);
    setSavedFilters(null);
    localStorage.removeItem(LOCAL_FILTERS);
  };

  const isDisabled = isPending || getActiveFiltersCount(filters) === 0;

  const isMobileSticky = variant === 'mobileSticky';
  const isMobileBar = variant === 'mobileBar';

  return (
    <>
      <StyledButton
        ref={isMobileSticky || isMobileBar ? undefined : searchProfileRef}
        disabled={isDisabled}
        variant="form-button"
        sx={{
          width: isMobileSticky ? 'auto' : fullWidth ? '100%' : null,
          borderColor: isDisabled ? COLORS['neutral/300'] : null,
          ...(isMobileSticky && {
            backgroundColor: COLORS.white,
            border: `2px solid ${COLORS['secondary/500']}`,
            borderRadius: '5px',
            color: COLORS['secondary/500'],
            padding: '12px 24px',
            minHeight: 48,
            height: 'auto',
            '&:hover': {
              backgroundColor: COLORS['secondary/50'],
              borderColor: COLORS['secondary/500'],
            },
            '& .MuiTypography-root': { color: COLORS['secondary/500'] },
            '& .MuiButton-startIcon': { marginRight: 0.75 },
            '& .MuiButton-startIcon svg': { color: COLORS['secondary/500'] },
          }),
          ...(isMobileBar && {
            height: '36px',
            minWidth: 0,
            width: '36px',
            padding: 0,
            border: `1px solid ${COLORS['neutral/300']}`,
            borderRadius: '10px',
            backgroundColor: COLORS.white,
            '&:hover': { backgroundColor: COLORS['neutral/50'] },
          }),
        }}
        onClick={handleOpenModal}
        startIcon={
          !isMobileBar &&
          (isMobileSticky ? <AddIcon sx={{ fontSize: 24 }} /> : <SearchProfileIcon />)
        }
      >
        {isMobileBar ? (
          <SearchProfileIcon />
        ) : (
          <Typography
            noWrap
            fontWeight={600}
            color={
              isDisabled
                ? COLORS['neutral/500']
                : isMobileSticky
                  ? COLORS['secondary/500']
                  : COLORS['primary/600']
            }
            textTransform={'initial'}
          >
            {t('searchProfile.buttons.save')}
          </Typography>
        )}
      </StyledButton>

      {!isLoading && (
        <StyledModal open={isOpen && !!user} onClose={handleCloseModal}>
          <form onSubmit={formik.handleSubmit} style={{ outline: 'none' }}>
            <ModalContent
              sx={{
                width: { xs: '90%', sm: '80%', md: '480px' },
              }}
            >
              <ModalHead>
                <Typography fontSize={24} fontWeight={600} lineHeight="34px">
                  {t('searchProfile.modal.title')}
                </Typography>
                <Button variant="transparent" onClick={handleCloseModal}>
                  <CrossIcon />
                </Button>
              </ModalHead>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <TextField
                  name="name"
                  size="small"
                  label={t('searchProfile.modal.name')}
                  placeholder={t('searchProfile.modal.namePlaceholder')}
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  inputProps={{ maxLength: 255 }}
                  sx={{ width: '100%', height: '44px' }}
                  error={!!formik.errors.name && formik.touched.name}
                  helperText={formik.touched.name ? formik.errors.name || '' : ''}
                />

                <Box>
                  <FormControlLabel
                    sx={{ width: 'fit-content', mb: '4px' }}
                    control={
                      <Switch
                        name="subscribe"
                        checked={formik.values.subscribe}
                        onChange={formik.handleChange}
                        color="primary"
                        sx={{
                          marginLeft: '8px',
                          '& .MuiSwitch-switchBase': {
                            '&.Mui-checked': {
                              color: COLORS.white,
                              '& + .MuiSwitch-track': {
                                backgroundColor: COLORS['primary/500'],
                                opacity: 0.8,
                              },
                            },
                          },
                          '& .MuiSwitch-track': {
                            backgroundColor: COLORS['neutral/500'],
                          },
                        }}
                      />
                    }
                    label={t('searchProfile.modal.isSubscribe')}
                  />
                  <Typography color={COLORS['neutral/700']} fontSize={14}>
                    {t('searchProfile.modal.subscribeDesc')}
                  </Typography>
                </Box>
              </Box>

              <ModalControlBox>
                <StyledControlButton variant="outline-secondary" onClick={handleCloseModal}>
                  <Typography noWrap fontWeight={600}>
                    {t('searchProfile.buttons.cancel')}
                  </Typography>
                </StyledControlButton>
                <StyledControlButton variant="primary" type="submit">
                  <Typography noWrap fontWeight={600}>
                    {t('searchProfile.buttons.saveShort')}
                  </Typography>
                </StyledControlButton>
              </ModalControlBox>
            </ModalContent>
          </form>
        </StyledModal>
      )}
    </>
  );
};
